import React, { useEffect, useRef, useState } from 'react';

/**
 * Auto-scrolling row of product screenshots.
 * Real dashboards shown in the first 3 seconds, no copy, just proof.
 * Duplicates the list so the CSS-animated track loops seamlessly.
 * Pauses on hover; respects prefers-reduced-motion.
 */
const ScreenshotMarquee = ({
  shots = [],
  speed = 60,      // seconds per full loop, higher = slower
  eyebrow,
  testid = 'screenshot-marquee',
}) => {
  const trackRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  if (!shots.length) return null;
  const loop = [...shots, ...shots];

  return (
    <section className="relative py-10 md:py-14 bg-white overflow-hidden" data-testid={testid}>
      {eyebrow && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{eyebrow}</p>
        </div>
      )}
      {/* fade edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />

      <div
        ref={trackRef}
        className="flex gap-4 md:gap-6 w-max"
        style={reduced ? {} : { animation: `videra-marquee ${speed}s linear infinite` }}
        onMouseEnter={(e) => { e.currentTarget.style.animationPlayState = 'paused'; }}
        onMouseLeave={(e) => { e.currentTarget.style.animationPlayState = 'running'; }}
      >
        {loop.map((s, i) => (
          <div
            key={`${s.src}-${i}`}
            className="flex-shrink-0 w-[280px] md:w-[400px] aspect-[16/10] rounded-xl overflow-hidden bg-slate-900 ring-1 ring-slate-900/10 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.35)]"
          >
            <img
              src={s.src}
              alt={s.alt || s.caption || 'Product screenshot'}
              loading={i < 4 ? 'eager' : 'lazy'}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes videra-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default ScreenshotMarquee;
